import { FlyoutMenu, SvgIcon } from '@bob/library'
import { CTA, ctaColorTheme } from '@websrc/App/Components/CTA'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { usePath } from '@websrc/App/Hooks/usePath'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { CTAType } from '@websrc/DataMappers/mapCTA'
import { mapServicebar } from '@websrc/DataMappers/mapServicebar'
import { mapTopbar } from '@websrc/DataMappers/mapTopbar'
import clsx from 'clsx'
import { Link } from 'gatsby'
import React, { useEffect } from 'react'

import { checkIsActive, isButton, isCTA } from '../MainTopbar.helpers'
import { dropdownTrigger, itemText, menuCTA } from './TopbarMenu.css'
import * as SC from './TopbarMenu.style'

export const TopbarMenu: React.FC<{
  items:
    | ReturnType<typeof mapTopbar>['menu_items' | 'left_menu_items']
    | ReturnType<typeof mapServicebar>['menu_items']
  showDropDownsOnRight?: boolean
  hidePrimaryCTAs?: boolean
  hideItems?: boolean
  primaryCTAsWidth?: (width: number) => void
}> = ({ items, showDropDownsOnRight, hideItems, hidePrimaryCTAs, primaryCTAsWidth }) => {
  const path = usePath()
  const locale = useLocale()

  const menuListItemRef = React.useRef<HTMLLIElement>(null)

  useEffect(() => {
    const sendWidthUpdate = () => {
      if (menuListItemRef.current && primaryCTAsWidth) {
        primaryCTAsWidth(menuListItemRef.current.offsetWidth || 0)
      }
    }

    window.addEventListener('resize', sendWidthUpdate)
    sendWidthUpdate()
    return () => window.removeEventListener('resize', sendWidthUpdate)
  }, [primaryCTAsWidth])

  return (
    <SC.MenuList>
      {items.map((menuItem, i) => {
        const isActive = checkIsActive(path, menuItem, locale)

        if (isButton(menuItem)) {
          return (
            <SC.MenuListItem key={i}>
              <Link
                className={clsx(
                  isActive ? 'isActive' : '',
                  itemText,
                  hideItems ? menuCTA.hidden : null
                )}
                data-text={menuItem.button.link.title}
                onClick={() =>
                  matomo.trackEvent(
                    'engagement',
                    'all',
                    ['top menu'],
                    [menuItem.button.link.title],
                    'menu'
                  )
                }
                to={menuItem.button.link.href}
              >
                {menuItem.button.link.title}
              </Link>
            </SC.MenuListItem>
          )
        }
        if (isCTA(menuItem)) {
          return (
            <SC.MenuListItem
              key={i}
              ref={menuItem.type === CTAType.PRIMARY ? menuListItemRef : null}
            >
              <CTA
                className={clsx(
                  isActive && 'isActive',
                  menuItem.type === CTAType.PRIMARY
                    ? 'coral-global-button m--type-fill m--theme-white m--size-sm'
                    : itemText,
                  menuItem.type === CTAType.PRIMARY && hidePrimaryCTAs && menuCTA.hidden,
                  menuItem.type !== CTAType.PRIMARY && hideItems && menuCTA.hidden,
                  menuItem.type === CTAType.PRIMARY && 'primaryCTA',
                  menuItem.type === CTAType.TERTIARY && menuItem.icon && menuCTA.linkWithIcon
                )}
                colorTheme={ctaColorTheme(menuItem.type)}
                cta={menuItem}
                data-text={menuItem?.text}
                onClick={() =>
                  matomo.trackEvent('engagement', 'all', ['top menu'], [menuItem?.text], 'menu')
                }
                prefix={
                  menuItem.icon ? (
                    <SvgIcon color={isActive ? 'blue6' : 'black'} name={menuItem.icon} size={18} />
                  ) : null
                }
                type={menuItem.type}
              />
            </SC.MenuListItem>
          )
        }

        if (!('dropdown' in menuItem)) return null

        const CTAContent = (
          <>
            <span
              className={clsx(itemText, isActive ? 'isActive' : '')}
              data-text={menuItem.dropdown.title}
            >
              {menuItem.dropdown.title}
            </span>
            <SvgIcon name={'ARROW_DOWN'} size={14} strokeWidth={4} />
          </>
        )

        const CTAItem = menuItem.dropdown.title_link ? (
          <Link
            className={dropdownTrigger}
            onClick={() =>
              matomo.trackEvent(
                'engagement',
                'all',
                ['top menu'],
                [menuItem.dropdown.title],
                'menu'
              )
            }
            to={menuItem.dropdown.title_link}
          >
            {CTAContent}
          </Link>
        ) : (
          <div className={dropdownTrigger}>{CTAContent}</div>
        )

        return (
          <SC.MenuListItem key={i}>
            <FlyoutMenu
              buttonElement={CTAItem}
              className={clsx(hideItems ? menuCTA.hidden : null)}
              showOnRight={showDropDownsOnRight}
            >
              <SC.DropdownWrapper>
                {menuItem.dropdown.links.map(({ title, href }, linkI) => (
                  <SC.DropdownItemWrapper key={linkI}>
                    <a
                      className={itemText}
                      data-text={title}
                      href={href}
                      onClick={() =>
                        matomo.trackEvent(
                          'engagement',
                          'all',
                          ['top menu'],
                          [menuItem.dropdown.title, title],
                          'menu'
                        )
                      }
                    >
                      {title}
                    </a>
                  </SC.DropdownItemWrapper>
                ))}
              </SC.DropdownWrapper>
            </FlyoutMenu>
          </SC.MenuListItem>
        )
      })}
    </SC.MenuList>
  )
}
